import { useEffect, useState, useSyncExternalStore } from "react";
import { isOnline } from "./online";
import type { LiveStatus } from "./types";

/**
 * The live status of this browser. `connection` is what the connection owner (this tab or the
 * leader tab) reports; the network state of this tab overrides it with "offline".
 */
let connection: LiveStatus = "connecting";
const listeners = new Set<() => void>();
const emit = () => {
  for (const l of [...listeners]) l();
};

export function setConnectionStatus(next: LiveStatus) {
  if (connection === next) return;
  connection = next;
  emit();
}

export const getConnectionStatus = () => connection;

export function currentLiveStatus(): LiveStatus {
  return isOnline() ? connection : "offline";
}

function subscribe(fn: () => void) {
  listeners.add(fn);
  window.addEventListener("online", fn);
  window.addEventListener("offline", fn);
  return () => {
    listeners.delete(fn);
    window.removeEventListener("online", fn);
    window.removeEventListener("offline", fn);
  };
}

/** `connecting | live | reconnecting | offline`. */
export function useLiveStatus(): LiveStatus {
  return useSyncExternalStore(subscribe, currentLiveStatus, () => "connecting");
}

/**
 * What the banner shows. Offline is certain (the browser says so) and shown at once; a reconnecting
 * stream is only worth a banner when it lasts: it appears after `delayMs` (3 s) of continuous trouble.
 */
export function useLiveBanner(delayMs = 3000): "offline" | "reconnecting" | null {
  const status = useLiveStatus();
  const [late, setLate] = useState(false);
  useEffect(() => {
    if (status !== "reconnecting") {
      setLate(false);
      return;
    }
    const t = setTimeout(() => setLate(true), delayMs);
    return () => clearTimeout(t);
  }, [status, delayMs]);
  if (status === "offline") return "offline";
  if (status === "reconnecting" && late) return "reconnecting";
  return null;
}
